<template>
  <div class="ad-metrics-panel">
    <div class="ad-metrics-panel__header">
      <div class="ad-metrics-panel__toolbar">
        <div class="ad-metrics-panel__toolbar-main">
          <h2 class="ad-metrics-panel__title mb-0">Метрики рекламы на домофоне</h2>

          <div class="ad-metrics-panel__filters">
            <v-btn-toggle
              v-model="cityId"
              mandatory
              dense
              rounded
              color="primary"
              class="ad-metrics-panel__toggle"
              @change="loadMetrics"
            >
              <v-btn
                v-for="option in cityOptions"
                :key="String(option.value)"
                :value="option.value"
                small
                class="text-none"
              >
                {{ option.label }}
              </v-btn>
            </v-btn-toggle>

            <div class="ad-metrics-panel__scope">
              <span class="ad-metrics-panel__scope-chip">
                <v-icon x-small class="mr-1">mdi-map-marker</v-icon>
                <template v-if="selectedRegionKey">
                  <span class="ad-metrics-panel__scope-city">{{ currentCityLabel }}</span>
                  <span class="ad-metrics-panel__scope-divider">/</span>
                  <span class="ad-metrics-panel__scope-region">{{ selectedRegionLabel }}</span>
                </template>
                <template v-else>
                  <span class="ad-metrics-panel__scope-city">{{ currentCityLabel }}</span>
                </template>
              </span>

              <v-tooltip v-if="selectedRegionKey" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    x-small
                    color="primary"
                    class="ad-metrics-panel__back-to-city"
                    v-bind="attrs"
                    v-on="on"
                    @click="clearRegionFilter"
                  >
                    <v-icon small>mdi-arrow-left-circle</v-icon>
                  </v-btn>
                </template>
                <span>Вернуться на город</span>
              </v-tooltip>
            </div>

            <v-menu
              v-model="periodMenu"
              :close-on-content-click="false"
              offset-y
              min-width="290"
              attach
              z-index="220"
              content-class="ad-metrics-panel__period-menu"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  text
                  small
                  class="text-none ad-metrics-panel__period-btn"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon left small>mdi-calendar-range</v-icon>
                  {{ periodLabelShort }}
                </v-btn>
              </template>
              <div class="ad-metrics-panel__period-menu-body pa-3">
                <v-date-picker
                  v-model="dateRange"
                  range
                  :max="maxDate"
                  :min="minDate"
                />
                <div class="d-flex justify-end mt-2">
                  <v-btn text small class="text-none" @click="resetPeriod">7 дней</v-btn>
                  <v-btn
                    color="primary"
                    text
                    small
                    class="text-none"
                    :disabled="dateRange.length < 2"
                    @click="applyPeriod"
                  >
                    Применить
                  </v-btn>
                </div>
              </div>
            </v-menu>
          </div>
        </div>

        <div class="ad-metrics-panel__toolbar-actions">
          <v-btn
            color="primary"
            outlined
            rounded
            small
            class="text-none"
            :loading="loading"
            :disabled="loading"
            @click="loadMetrics"
          >
            <v-icon left small>mdi-refresh</v-icon>
            Обновить
          </v-btn>

          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="primary"
                rounded
                small
                class="text-none"
                :loading="exporting"
                :disabled="loading || exporting || !days.length"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon left small>mdi-download</v-icon>
                CSV
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="downloadCsv('summary')">
                <v-list-item-title>Сводка по дням</v-list-item-title>
              </v-list-item>
              <v-list-item @click="downloadCsv('regions')">
                <v-list-item-title>По районам</v-list-item-title>
              </v-list-item>
              <v-list-item @click="downloadCsv('hourly')">
                <v-list-item-title>Пики по часам</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>

      <p class="ad-metrics-panel__hint">
        Показатели пересчитываются раз в сутки и хранятся 14 дней.
      </p>
    </div>

    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

    <template v-else>
      <v-alert
        v-if="error"
        type="error"
        dense
        text
        class="mb-4"
      >
        {{ error }}
      </v-alert>

      <v-alert
        v-if="!latestDay && !error"
        type="info"
        dense
        text
        class="mb-4"
      >
        Нет предрасчитанных данных за выбранный период. Метрики собираются ежедневно.
      </v-alert>

      <v-row v-if="scopedLatestDay" class="mb-2 ad-metrics-panel__kpi-row" dense>
        <v-col
          v-for="card in kpiCards"
          :key="card.key"
          class="ad-metrics-panel__kpi-col"
        >
          <v-tooltip bottom max-width="280">
            <template v-slot:activator="{ on, attrs }">
              <v-card
                class="ad-metrics-panel__kpi"
                flat
                outlined
                v-bind="attrs"
                v-on="on"
              >
                <v-card-text class="ad-metrics-panel__kpi-body">
                  <div class="ad-metrics-panel__kpi-title">{{ card.label }}</div>
                  <div class="ad-metrics-panel__kpi-value">{{ formatNumber(card.value) }}</div>
                  <div v-if="card.hint" class="ad-metrics-panel__kpi-hint">{{ card.hint }}</div>
                </v-card-text>
              </v-card>
            </template>
            <span>{{ card.tooltip }}</span>
          </v-tooltip>
        </v-col>
      </v-row>

      <p v-if="scopedLatestDay" class="ad-metrics-panel__kpi-note">
        Домофоны, калитки, дома, квартиры — на {{ formatChartDate(scopedLatestDay.date) }}.
        Открытия (журнал / приложение), прослушивания и уник. слушатели — сумма за {{ periodDaysLabel }}
        ({{ formatChartDate(dateFrom) }} — {{ formatChartDate(dateTo) }}).
        Прослушивания = журнал + приложение.
        <span v-if="scopedLatestDay.household_size">
          · S = {{ scopedLatestDay.household_size }} чел./квартира
        </span>
        <span v-if="selectedRegionKey">
          · район: {{ selectedRegionLabel }}
        </span>
        <span v-else-if="isAllCities && days.length">
          · по всем городам (сумма)
        </span>
      </p>

      <v-row>
        <v-col cols="12" lg="7">
          <v-card flat outlined class="ad-metrics-panel__chart-card pa-4 mb-4">
            <div class="ad-metrics-panel__chart-header">
              <h3 class="ad-metrics-panel__chart-title">Динамика по дням</h3>
              <div class="ad-metrics-panel__series">
                <v-chip
                  v-for="series in dailySeriesOptions"
                  :key="series.key"
                  class="mr-2 mb-1 ad-metrics-panel__series-chip"
                  :class="{ 'ad-metrics-panel__series-chip--off': !visibleDailySeries[series.key] }"
                  :color="series.color"
                  :outlined="!visibleDailySeries[series.key]"
                  :text-color="visibleDailySeries[series.key] ? 'white' : series.color"
                  label
                  small
                  @click="toggleDailySeries(series.key)"
                >
                  {{ series.label }}
                </v-chip>
              </div>
            </div>
            <div v-if="scopedDays.length && hasVisibleDailySeries" class="ad-metrics-panel__canvas-wrap">
              <canvas ref="dailyChart" />
            </div>
            <div v-else class="ad-metrics-panel__empty">
              {{ emptyDailyMessage }}
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" lg="5">
          <v-card flat outlined class="ad-metrics-panel__chart-card pa-4 mb-4">
            <div class="ad-metrics-panel__chart-header">
              <h3 class="ad-metrics-panel__chart-title">Пики открытий по часам</h3>
              <v-select
                v-model="selectedDetailDate"
                :items="detailDateOptions"
                dense
                outlined
                hide-details
                class="ad-metrics-panel__day-select"
                :disabled="!detailDateOptions.length"
              />
            </div>
            <div v-if="hourlyData.length" class="ad-metrics-panel__canvas-wrap ad-metrics-panel__canvas-wrap--short">
              <canvas ref="hourlyChart" />
            </div>
            <div v-else class="ad-metrics-panel__empty ad-metrics-panel__empty--short">
              Нет почасовых данных за выбранный день
            </div>
          </v-card>
        </v-col>
      </v-row>

      <div class="ad-metrics-panel__regions">
        <div class="ad-metrics-panel__regions-header mb-3">
          <h3 class="ad-metrics-panel__chart-title mb-0">Разбивка по районам</h3>
          <div class="ad-metrics-panel__regions-header-right">
            <div
              v-if="selectedDetailDate && regionsTotals.doorphones"
              class="ad-metrics-panel__regions-meta"
            >
              <span class="ad-metrics-panel__regions-meta-date">
                {{ formatChartDate(selectedDetailDate) }}
              </span>
              <span class="ad-metrics-panel__regions-meta-sep">·</span>
              <span>{{ formatNumber(regionsTotals.doorphones) }} домофонов</span>
              <span class="ad-metrics-panel__regions-meta-sep">·</span>
              <span>{{ formatNumber(regionsTotals.gates) }} калиток</span>
              <span class="ad-metrics-panel__regions-meta-sep">·</span>
              <span>{{ formatNumber(regionsTotals.houses) }} домов</span>
              <span class="ad-metrics-panel__regions-meta-sep">·</span>
              <span>{{ formatNumber(regionsTotals.apartments) }} квартир</span>
            </div>
          </div>
        </div>

        <div v-if="!regionsForSelectedDay.length" class="ad-metrics-panel__empty ad-metrics-panel__empty--regions">
          Нет данных по районам
        </div>

        <v-row v-else dense>
          <v-col
            v-for="(region, index) in regionsWithShare"
            :key="`${region.name}-${index}`"
            cols="12"
            sm="6"
          >
            <v-card
              class="ad-metrics-panel__region-card"
              :class="{
                'ad-metrics-panel__region-card--active': isRegionSelected(region),
              }"
              flat
              outlined
              @click="selectRegion(region)"
            >
              <div
                class="ad-metrics-panel__region-accent"
                :style="{ background: regionAccent(index) }"
              />
              <v-card-text class="pa-3">
                <div class="ad-metrics-panel__region-top">
                  <div class="ad-metrics-panel__region-name">{{ region.name }}</div>
                  <v-chip
                    x-small
                    label
                    :color="regionAccent(index)"
                    text-color="white"
                    class="ad-metrics-panel__region-share-chip"
                  >
                    {{ region.apartmentsShare }}% квартир
                  </v-chip>
                </div>

                <div class="ad-metrics-panel__region-metrics">
                  <v-tooltip bottom max-width="240">
                    <template v-slot:activator="{ on, attrs }">
                      <div
                        class="ad-metrics-panel__region-metric ad-metrics-panel__region-metric--interactive"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon small color="primary">mdi-doorbell-video</v-icon>
                        <span class="ad-metrics-panel__region-metric-value">
                          {{ formatNumber(region.doorphones_count) }}
                        </span>
                        <span class="ad-metrics-panel__region-metric-label">домофоны</span>
                        <span class="ad-metrics-panel__region-metric-share">
                          {{ region.doorphonesShare }}%
                        </span>
                      </div>
                    </template>
                    <span>{{ regionMetricTooltip(region, 'doorphones') }}</span>
                  </v-tooltip>

                  <v-tooltip bottom max-width="240">
                    <template v-slot:activator="{ on, attrs }">
                      <div
                        class="ad-metrics-panel__region-metric ad-metrics-panel__region-metric--interactive"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon small color="purple darken-1">mdi-gate</v-icon>
                        <span class="ad-metrics-panel__region-metric-value">
                          {{ formatNumber(region.gates_count) }}
                        </span>
                        <span class="ad-metrics-panel__region-metric-label">калитки</span>
                        <span class="ad-metrics-panel__region-metric-share">
                          {{ region.gatesShare }}%
                        </span>
                      </div>
                    </template>
                    <span>{{ regionMetricTooltip(region, 'gates') }}</span>
                  </v-tooltip>

                  <v-tooltip bottom max-width="240">
                    <template v-slot:activator="{ on, attrs }">
                      <div
                        class="ad-metrics-panel__region-metric ad-metrics-panel__region-metric--interactive"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon small color="orange darken-2">mdi-home-city-outline</v-icon>
                        <span class="ad-metrics-panel__region-metric-value">
                          {{ formatNumber(region.houses_count) }}
                        </span>
                        <span class="ad-metrics-panel__region-metric-label">дома</span>
                        <span class="ad-metrics-panel__region-metric-share">
                          {{ region.housesShare }}%
                        </span>
                      </div>
                    </template>
                    <span>{{ regionMetricTooltip(region, 'houses') }}</span>
                  </v-tooltip>

                  <v-tooltip bottom max-width="240">
                    <template v-slot:activator="{ on, attrs }">
                      <div
                        class="ad-metrics-panel__region-metric ad-metrics-panel__region-metric--interactive"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon small color="green darken-1">mdi-door</v-icon>
                        <span class="ad-metrics-panel__region-metric-value">
                          {{ formatNumber(region.apartments_count) }}
                        </span>
                        <span class="ad-metrics-panel__region-metric-label">квартиры</span>
                        <span class="ad-metrics-panel__region-metric-share">
                          {{ region.apartmentsShare }}%
                        </span>
                      </div>
                    </template>
                    <span>{{ regionMetricTooltip(region, 'apartments') }}</span>
                  </v-tooltip>
                </div>

                <div class="ad-metrics-panel__region-activity">
                  <div class="ad-metrics-panel__region-activity-item">
                    <span class="ad-metrics-panel__region-activity-label">Журнал</span>
                    <span class="ad-metrics-panel__region-activity-value">
                      {{ formatNumber(region.opens_journal_count) }}
                    </span>
                  </div>
                  <div class="ad-metrics-panel__region-activity-item">
                    <span class="ad-metrics-panel__region-activity-label">Приложение</span>
                    <span class="ad-metrics-panel__region-activity-value">
                      {{ formatNumber(region.opens_app_count) }}
                    </span>
                  </div>
                  <div class="ad-metrics-panel__region-activity-item">
                    <span class="ad-metrics-panel__region-activity-label">Прослуш.</span>
                    <span class="ad-metrics-panel__region-activity-value">
                      {{ formatNumber(region.listens_count) }}
                    </span>
                  </div>
                  <div class="ad-metrics-panel__region-activity-item">
                    <span class="ad-metrics-panel__region-activity-label">Уник. U</span>
                    <span class="ad-metrics-panel__region-activity-value">
                      {{ formatNumber(region.unique_listeners) }}
                    </span>
                  </div>
                </div>

                <div class="ad-metrics-panel__region-bars">
                  <div class="ad-metrics-panel__region-bar-row">
                    <span>Домофоны</span>
                    <span>{{ region.doorphonesShare }}%</span>
                  </div>
                  <v-progress-linear
                    :value="region.doorphonesShare"
                    height="4"
                    rounded
                    color="primary"
                    background-color="#e3eaf3"
                    class="mb-1"
                  />
                  <div class="ad-metrics-panel__region-bar-row">
                    <span>Квартиры</span>
                    <span>{{ region.apartmentsShare }}%</span>
                  </div>
                  <v-progress-linear
                    :value="region.apartmentsShare"
                    height="4"
                    rounded
                    color="success"
                    background-color="#e8f5e9"
                  />
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </template>
  </div>
</template>

<script>
import Chart from 'chart.js';
import { mapActions } from 'vuex';
import ruDateFormatMixin from '@/mixins/ruDateFormat';

const CITY_ULYANOVSK = 1;
const CITY_SARANSK = 2;

const DAILY_SERIES = [
  {
    key: 'opens_journal',
    label: 'Журнал (ключ/код)',
    field: 'opens_journal_count',
    color: 'rgba(25, 118, 210, 1)',
    background: 'rgba(25, 118, 210, 0.12)',
  },
  {
    key: 'opens_app',
    label: 'Приложение',
    field: 'opens_app_count',
    color: 'rgba(156, 39, 176, 1)',
    background: 'rgba(156, 39, 176, 0.12)',
  },
  {
    key: 'listens',
    label: 'Прослушивания',
    field: 'listens_count',
    color: 'rgba(255, 152, 0, 1)',
    background: 'rgba(255, 152, 0, 0.12)',
  },
  {
    key: 'unique',
    label: 'Уник. слушатели',
    field: 'unique_listeners',
    color: 'rgba(76, 175, 80, 1)',
    background: 'rgba(76, 175, 80, 0.12)',
  },
];

const pointValueLabelsPlugin = {
  afterDatasetsDraw (chart) {
    const { ctx } = chart;
    if (!ctx) return;

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (!meta || meta.hidden) return;

      meta.data.forEach((element, index) => {
        const raw = dataset.data[index];
        if (raw == null || raw === '') return;

        const model = element._model || {};
        if (model.x == null || model.y == null) return;

        ctx.save();
        ctx.font = 'bold 11px Montserrat, Arial, sans-serif';
        ctx.fillStyle = dataset.borderColor || dataset.backgroundColor || '#37474f';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText(String(raw), model.x, model.y - 6);
        ctx.restore();
      });
    });
  },
};

const MONTHS_GENITIVE = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

function toIsoDate (date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function yesterdayIsoDate () {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  return toIsoDate(date);
}

function daysAgoIsoDate (days) {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return toIsoDate(date);
}

function shiftIsoDate (isoDate, deltaDays) {
  const date = new Date(`${isoDate}T12:00:00`);
  date.setDate(date.getDate() + deltaDays);
  return toIsoDate(date);
}

const DEFAULT_PERIOD_DAYS = 6;
const PRESET_7_DAYS = 7;
const MAX_PERIOD_DAYS = 14;

function defaultPeriodRange (daysBack = DEFAULT_PERIOD_DAYS) {
  const dateTo = yesterdayIsoDate();
  const dateFrom = shiftIsoDate(dateTo, -daysBack);
  return { dateFrom, dateTo };
}

export default {
  name: 'DoorphoneAdMetricsPanel',
  mixins: [ruDateFormatMixin],
  data () {
    const { dateFrom, dateTo } = defaultPeriodRange();

    return {
      loading: false,
      exporting: false,
      error: null,
      days: [],
      cityId: CITY_ULYANOVSK,
      dateFrom,
      dateTo,
      dateRange: [dateFrom, dateTo],
      periodMenu: false,
      selectedDetailDate: null,
      selectedRegionKey: null,
      dailyChart: null,
      hourlyChart: null,
      cityOptions: [
        { value: CITY_ULYANOVSK, label: 'Ульяновск' },
        { value: CITY_SARANSK, label: 'Саранск' },
        { value: 'all', label: 'Все' },
      ],
      dailySeriesOptions: DAILY_SERIES,
      visibleDailySeries: {
        opens_journal: true,
        opens_app: true,
        listens: true,
        unique: true,
      },
      regionAccentColors: [
        '#1976d2',
        '#43a047',
        '#fb8c00',
        '#8e24aa',
        '#00897b',
        '#e53935',
        '#5c6bc0',
        '#6d4c41',
      ],
    };
  },
  computed: {
    isAllCities () {
      return this.cityId === 'all';
    },
    apiCityId () {
      return this.isAllCities ? null : this.cityId;
    },
    maxDate () {
      return yesterdayIsoDate();
    },
    minDate () {
      return daysAgoIsoDate(MAX_PERIOD_DAYS + 1);
    },
    periodLabelShort () {
      return `${this.formatShortDate(this.dateFrom)}–${this.formatShortDate(this.dateTo)}`;
    },
    aggregatedDays () {
      const byDate = {};

      this.days.forEach((day) => {
        const key = day.date;
        if (!byDate[key]) {
          byDate[key] = {
            date: day.date,
            doorphones_count: 0,
            gates_count: 0,
            houses_count: 0,
            apartments_count: 0,
            opens_journal_count: 0,
            opens_app_count: 0,
            opens_count: 0,
            listens_count: 0,
            unique_listeners: 0,
            household_size: day.household_size,
            regions: [],
            hourly_opens: Array.from({ length: 24 }, (_, hour) => ({ hour, opens_count: 0 })),
          };
        }

        const bucket = byDate[key];
        bucket.doorphones_count += Number(day.doorphones_count) || 0;
        bucket.gates_count += Number(day.gates_count) || 0;
        bucket.houses_count += Number(day.houses_count) || 0;
        bucket.apartments_count += Number(day.apartments_count) || 0;
        bucket.opens_journal_count += Number(day.opens_journal_count) || 0;
        bucket.opens_app_count += Number(day.opens_app_count) || 0;
        bucket.opens_count += Number(day.opens_count) || 0;
        bucket.listens_count += Number(day.listens_count) || 0;
        bucket.unique_listeners += Number(day.unique_listeners) || 0;
        bucket.household_size = day.household_size || bucket.household_size;

        (day.regions || []).forEach((region) => {
          bucket.regions.push({
            ...region,
            city_id: day.city_id,
            filter_key: this.regionFilterKey(day.city_id, region.region_id),
            name: this.isAllCities && day.city_id
              ? `${region.name || 'Без района'} (${this.cityLabel(day.city_id)})`
              : (region.name || 'Без района'),
          });
        });

        (day.hourly_opens || []).forEach((item) => {
          const hour = Number(item.hour);
          if (hour >= 0 && hour <= 23) {
            bucket.hourly_opens[hour].opens_count += Number(item.opens_count) || 0;
          }
        });
      });

      return Object.values(byDate).sort((a, b) => (a.date > b.date ? 1 : -1));
    },
    latestDay () {
      if (!this.aggregatedDays.length) return null;
      return this.aggregatedDays[this.aggregatedDays.length - 1];
    },
    regionFilterOptions () {
      const options = [{ text: 'Весь город', value: null }];
      const seen = new Set();

      this.aggregatedDays.forEach((day) => {
        (day.regions || []).forEach((region) => {
          const key = region.filter_key || this.regionFilterKey(region.city_id, region.region_id);
          if (seen.has(key)) return;
          seen.add(key);
          options.push({
            text: region.name || 'Без района',
            value: key,
          });
        });
      });

      return options;
    },
    selectedRegionLabel () {
      const option = this.regionFilterOptions.find((item) => item.value === this.selectedRegionKey);
      return option?.text || '';
    },
    currentCityLabel () {
      if (this.isAllCities) return 'Все города';
      const option = this.cityOptions.find((item) => item.value === this.cityId);
      return option?.label || 'Город';
    },
    scopedDays () {
      if (!this.selectedRegionKey) {
        return this.aggregatedDays;
      }

      return this.aggregatedDays.map((day) => {
        const region = (day.regions || []).find(
          (item) => (item.filter_key || this.regionFilterKey(item.city_id, item.region_id)) === this.selectedRegionKey,
        );

        if (!region) {
          return {
            date: day.date,
            doorphones_count: 0,
            gates_count: 0,
            houses_count: 0,
            apartments_count: 0,
            opens_journal_count: 0,
            opens_app_count: 0,
            opens_count: 0,
            listens_count: 0,
            unique_listeners: 0,
            household_size: day.household_size,
            regions: day.regions,
            hourly_opens: Array.from({ length: 24 }, (_, hour) => ({ hour, opens_count: 0 })),
          };
        }

        const hourly = Array.isArray(region.hourly_opens) && region.hourly_opens.length
          ? region.hourly_opens
          : Array.from({ length: 24 }, (_, hour) => ({ hour, opens_count: 0 }));

        return {
          date: day.date,
          doorphones_count: Number(region.doorphones_count) || 0,
          gates_count: Number(region.gates_count) || 0,
          houses_count: Number(region.houses_count) || 0,
          apartments_count: Number(region.apartments_count) || 0,
          opens_journal_count: Number(region.opens_journal_count) || 0,
          opens_app_count: Number(region.opens_app_count) || 0,
          opens_count: Number(region.opens_count) || 0,
          listens_count: Number(region.listens_count) || 0,
          unique_listeners: Number(region.unique_listeners) || 0,
          household_size: day.household_size,
          regions: day.regions,
          hourly_opens: hourly,
        };
      });
    },
    scopedLatestDay () {
      if (!this.scopedDays.length) return null;
      return this.scopedDays[this.scopedDays.length - 1];
    },
    detailDateOptions () {
      return this.scopedDays.map((day) => ({
        text: this.formatChartDate(day.date),
        value: day.date,
      }));
    },
    selectedDetailDay () {
      if (!this.selectedDetailDate) return this.scopedLatestDay;
      return this.scopedDays.find((day) => day.date === this.selectedDetailDate) || this.scopedLatestDay;
    },
    periodDaysCount () {
      if (!this.dateFrom || !this.dateTo) return this.aggregatedDays.length || 0;
      const from = new Date(`${this.dateFrom}T00:00:00`);
      const to = new Date(`${this.dateTo}T00:00:00`);
      if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
        return this.aggregatedDays.length || 0;
      }
      const diff = Math.round((to - from) / 86400000);
      return Math.max(1, diff + 1);
    },
    periodDaysLabel () {
      const n = this.periodDaysCount;
      const mod10 = n % 10;
      const mod100 = n % 100;
      let word = 'дней';
      if (mod10 === 1 && mod100 !== 11) word = 'день';
      else if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) word = 'дня';
      return `${n} ${word}`;
    },
    periodActivityTotals () {
      return this.scopedDays.reduce((acc, day) => {
        acc.opensJournal += Number(day.opens_journal_count) || 0;
        acc.opensApp += Number(day.opens_app_count) || 0;
        acc.opens += Number(day.opens_count) || 0;
        acc.listens += Number(day.listens_count) || 0;
        acc.unique += Number(day.unique_listeners) || 0;
        return acc;
      }, { opensJournal: 0, opensApp: 0, opens: 0, listens: 0, unique: 0 });
    },
    kpiCards () {
      const day = this.scopedLatestDay || {};
      const totals = this.periodActivityTotals;
      const snapshotDate = day.date ? this.formatChartDate(day.date) : '—';
      const periodTooltip = `Сумма за выбранный период: ${this.periodDaysLabel}` +
        ` (${this.formatChartDate(this.dateFrom)} — ${this.formatChartDate(this.dateTo)})`;
      const snapshotTooltip = `Значение на последний день периода: ${snapshotDate}`;
      const scopeHint = this.selectedRegionKey
        ? ` Район: ${this.selectedRegionLabel}.`
        : '';

      return [
        {
          key: 'doorphones',
          label: 'Домофоны',
          value: day.doorphones_count || 0,
          tooltip: `${snapshotTooltip}.${scopeHint}`,
        },
        {
          key: 'gates',
          label: 'Калитки',
          value: day.gates_count || 0,
          tooltip: `${snapshotTooltip}. Калитки с поддержкой загрузки аудио.${scopeHint}`,
        },
        {
          key: 'houses',
          label: 'Дома',
          value: day.houses_count || 0,
          tooltip: `${snapshotTooltip}. Уникальные дома по домофонам.${scopeHint}`,
        },
        {
          key: 'apartments',
          label: 'Квартиры (Q)',
          value: day.apartments_count || 0,
          tooltip: `${snapshotTooltip}.${scopeHint}`,
        },
        {
          key: 'opens_journal',
          label: 'Открытия (журнал)',
          value: totals.opensJournal,
          tooltip: `${periodTooltip}. Ключ / код с устройства.${scopeHint}`,
        },
        {
          key: 'opens_app',
          label: 'Открытия (приложение)',
          value: totals.opensApp,
          tooltip: `${periodTooltip}. Журнал (мобильное приложение).${scopeHint}`,
        },
        {
          key: 'listens',
          label: 'Прослушивания',
          value: totals.listens,
          hint: 'журнал + приложение',
          tooltip: `${periodTooltip}. Любое открытие = прослушивание.${scopeHint}`,
        },
        {
          key: 'unique',
          label: 'Уник. слушатели (U)',
          value: totals.unique,
          tooltip: `${periodTooltip}.${scopeHint}`,
        },
      ];
    },
    regionsForSelectedDay () {
      // Карточки районов всегда по выбранному дню города (не сужаем список фильтром).
      const day = this.aggregatedDays.find((item) => item.date === this.selectedDetailDate) || this.latestDay;
      return day?.regions || [];
    },
    regionsTotals () {
      return this.regionsForSelectedDay.reduce((acc, region) => {
        acc.doorphones += Number(region.doorphones_count) || 0;
        acc.gates += Number(region.gates_count) || 0;
        acc.houses += Number(region.houses_count) || 0;
        acc.apartments += Number(region.apartments_count) || 0;
        return acc;
      }, { doorphones: 0, gates: 0, houses: 0, apartments: 0 });
    },
    regionsWithShare () {
      const totals = this.regionsTotals;
      const list = this.regionsForSelectedDay.map((region) => {
        const doorphones = Number(region.doorphones_count) || 0;
        const gates = Number(region.gates_count) || 0;
        const houses = Number(region.houses_count) || 0;
        const apartments = Number(region.apartments_count) || 0;
        return {
          ...region,
          opens_journal_count: Number(region.opens_journal_count) || 0,
          opens_app_count: Number(region.opens_app_count) || 0,
          listens_count: Number(region.listens_count) || 0,
          unique_listeners: Number(region.unique_listeners) || 0,
          doorphonesShare: totals.doorphones
            ? Math.round((doorphones / totals.doorphones) * 100)
            : 0,
          gatesShare: totals.gates
            ? Math.round((gates / totals.gates) * 100)
            : 0,
          housesShare: totals.houses
            ? Math.round((houses / totals.houses) * 100)
            : 0,
          apartmentsShare: totals.apartments
            ? Math.round((apartments / totals.apartments) * 100)
            : 0,
        };
      });

      return list.sort((a, b) => b.apartments_count - a.apartments_count);
    },
    hourlyData () {
      return this.selectedDetailDay?.hourly_opens || [];
    },
    hasVisibleDailySeries () {
      return this.dailySeriesOptions.some((series) => this.visibleDailySeries[series.key]);
    },
    emptyDailyMessage () {
      if (!this.scopedDays.length) {
        return 'Нет предрасчитанных данных за выбранный период';
      }
      if (!this.hasVisibleDailySeries) {
        return 'Включите хотя бы одну серию на графике';
      }
      return 'Нет данных';
    },
  },
  watch: {
    aggregatedDays: {
      deep: true,
      handler (value) {
        if (!value.length) {
          this.selectedDetailDate = null;
          this.selectedRegionKey = null;
          this.destroyCharts();
          return;
        }
        const dates = value.map((day) => day.date);
        if (!dates.includes(this.selectedDetailDate)) {
          this.selectedDetailDate = dates[dates.length - 1];
        }
        const regionKeys = this.regionFilterOptions
          .map((item) => item.value)
          .filter((key) => key != null);
        if (this.selectedRegionKey && !regionKeys.includes(this.selectedRegionKey)) {
          this.selectedRegionKey = null;
        }
        this.$nextTick(() => this.renderCharts());
      },
    },
    selectedDetailDate () {
      this.$nextTick(() => this.renderHourlyChart());
    },
    selectedRegionKey () {
      this.$nextTick(() => this.renderCharts());
    },
    visibleDailySeries: {
      deep: true,
      handler () {
        this.$nextTick(() => this.renderDailyChart());
      },
    },
  },
  mounted () {
    this.loadMetrics();
  },
  beforeDestroy () {
    this.destroyCharts();
  },
  methods: {
    ...mapActions({
      getDoorphoneAdMetrics: 'Reports/getDoorphoneAdMetrics',
      exportDoorphoneAdMetricsCsv: 'Reports/exportDoorphoneAdMetricsCsv',
    }),
    cityLabel (cityId) {
      if (cityId === CITY_ULYANOVSK) return 'Ульяновск';
      if (cityId === CITY_SARANSK) return 'Саранск';
      return `город ${cityId}`;
    },
    regionFilterKey (cityId, regionId) {
      const cityPart = cityId == null ? 'all' : String(cityId);
      const regionPart = regionId == null ? 'null' : String(regionId);
      return `${cityPart}:${regionPart}`;
    },
    selectRegion (region) {
      const key = region.filter_key || this.regionFilterKey(region.city_id, region.region_id);
      this.selectedRegionKey = key;
    },
    clearRegionFilter () {
      this.selectedRegionKey = null;
    },
    isRegionSelected (region) {
      if (!this.selectedRegionKey) return false;
      const key = region.filter_key || this.regionFilterKey(region.city_id, region.region_id);
      return this.selectedRegionKey === key;
    },
    regionAccent (index) {
      return this.regionAccentColors[index % this.regionAccentColors.length];
    },
    regionMetricTooltip (region, type) {
      const totals = this.regionsTotals;
      const labels = {
        doorphones: {
          name: 'домофонов',
          count: region.doorphones_count,
          share: region.doorphonesShare,
          total: totals.doorphones,
        },
        gates: {
          name: 'калиток',
          count: region.gates_count,
          share: region.gatesShare,
          total: totals.gates,
        },
        houses: {
          name: 'домов',
          count: region.houses_count,
          share: region.housesShare,
          total: totals.houses,
        },
        apartments: {
          name: 'квартир',
          count: region.apartments_count,
          share: region.apartmentsShare,
          total: totals.apartments,
        },
      };
      const item = labels[type];
      if (!item) return '';

      return `${this.formatNumber(item.count)} из ${this.formatNumber(item.total)} ${item.name} ` +
        `(${item.share}% по городу за выбранный день)`;
    },
    formatNumber (value) {
      if (value === null || value === undefined) return '0';
      return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    },
    formatChartDate (dateString) {
      return this.ruDateFormat(dateString, true);
    },
    formatShortDate (dateString) {
      if (!dateString) return '';
      const parts = String(dateString).split('-');
      if (parts.length !== 3) return this.formatChartDate(dateString);
      const day = Number(parts[2]);
      const monthIndex = Number(parts[1]) - 1;
      const monthName = MONTHS_GENITIVE[monthIndex];
      if (!day || !monthName) return this.formatChartDate(dateString);
      return `${day} ${monthName}`;
    },
    resetPeriod () {
      const { dateFrom, dateTo } = defaultPeriodRange(PRESET_7_DAYS);
      this.dateTo = dateTo;
      this.dateFrom = dateFrom;
      this.dateRange = [dateFrom, dateTo];
      this.periodMenu = false;
      this.loadMetrics();
    },
    applyPeriod () {
      if (!Array.isArray(this.dateRange) || this.dateRange.length < 2) return;
      const sorted = [...this.dateRange].sort();
      this.dateFrom = sorted[0];
      this.dateTo = sorted[1];
      this.periodMenu = false;
      this.loadMetrics();
    },
    toggleDailySeries (key) {
      this.$set(this.visibleDailySeries, key, !this.visibleDailySeries[key]);
    },
    async loadMetrics () {
      this.loading = true;
      this.error = null;
      this.selectedRegionKey = null;

      try {
        const response = await this.getDoorphoneAdMetrics({
          dateFrom: this.dateFrom,
          dateTo: this.dateTo,
          cityId: this.apiCityId,
        });
        const payload = response?.data || response || {};
        this.days = this.filterDaysByCity(Array.isArray(payload.days) ? payload.days : []);
        this.dateRange = [this.dateFrom, this.dateTo];
      } catch (e) {
        this.days = [];
        this.error = 'Не удалось загрузить метрики';
      } finally {
        this.loading = false;
        this.$nextTick(() => this.renderCharts());
      }
    },
    filterDaysByCity (days) {
      if (this.apiCityId == null) return days;
      return days.filter((day) => Number(day.city_id) === Number(this.apiCityId));
    },
    async downloadCsv (section) {
      this.exporting = true;
      try {
        const blob = await this.exportDoorphoneAdMetricsCsv({
          dateFrom: this.dateFrom,
          dateTo: this.dateTo,
          cityId: this.apiCityId,
          section,
        });
        const link = document.createElement('a');
        link.download = `doorphone-ad-metrics-${section}.csv`;
        link.href = URL.createObjectURL(new Blob([blob], { type: 'application/csv' }));
        link.click();
        URL.revokeObjectURL(link.href);
      } catch (e) {
        this.error = 'Не удалось скачать CSV';
      } finally {
        this.exporting = false;
      }
    },
    renderCharts () {
      this.renderDailyChart();
      this.renderHourlyChart();
    },
    renderDailyChart () {
      if (!this.$refs.dailyChart || !this.scopedDays.length || !this.hasVisibleDailySeries) {
        this.destroyDailyChart();
        return;
      }

      const visibleSeries = this.dailySeriesOptions.filter(
        (series) => this.visibleDailySeries[series.key],
      );

      const chartData = {
        labels: this.scopedDays.map((day) => this.formatChartDate(day.date)),
        datasets: visibleSeries.map((series) => ({
          label: series.label,
          data: this.scopedDays.map((day) => day[series.field]),
          borderColor: series.color,
          backgroundColor: series.background,
          borderWidth: 2,
          fill: false,
          lineTension: 0.2,
          pointRadius: 5,
          pointHoverRadius: 8,
          pointHitDetectionRadius: 12,
        })),
      };

      this.destroyDailyChart();
      this.dailyChart = new Chart(this.$refs.dailyChart, {
        type: 'line',
        data: chartData,
        options: {
          maintainAspectRatio: false,
          responsive: true,
          layout: {
            padding: { top: 18, right: 8 },
          },
          legend: { display: false },
          hover: {
            mode: 'nearest',
            intersect: true,
          },
          tooltips: {
            enabled: true,
            mode: 'nearest',
            intersect: true,
            position: 'nearest',
            caretPadding: 6,
            caretSize: 6,
            xPadding: 10,
            yPadding: 8,
            displayColors: true,
            backgroundColor: 'rgba(38, 50, 56, 0.92)',
            titleFontFamily: 'Montserrat, Arial, sans-serif',
            bodyFontFamily: 'Montserrat, Arial, sans-serif',
            titleFontSize: 12,
            bodyFontSize: 12,
            callbacks: {
              title (tooltipItems) {
                if (!tooltipItems.length) return '';
                return tooltipItems[0].xLabel || '';
              },
              label (tooltipItem, data) {
                const dataset = data.datasets[tooltipItem.datasetIndex] || {};
                return `${dataset.label || ''}: ${tooltipItem.yLabel}`;
              },
            },
          },
          scales: {
            xAxes: [{ gridLines: { display: false } }],
            yAxes: [{
              ticks: { beginAtZero: true, precision: 0 },
            }],
          },
        },
        plugins: [pointValueLabelsPlugin],
      });
    },
    renderHourlyChart () {
      if (!this.$refs.hourlyChart || !this.hourlyData.length) {
        this.destroyHourlyChart();
        return;
      }

      const labels = this.hourlyData.map((item) => `${String(item.hour).padStart(2, '0')}:00`);
      const values = this.hourlyData.map((item) => Number(item.opens_count) || 0);

      this.destroyHourlyChart();
      this.hourlyChart = new Chart(this.$refs.hourlyChart, {
        type: 'bar',
        data: {
          labels,
          datasets: [{
            label: 'Открытия',
            data: values,
            backgroundColor: 'rgba(25, 118, 210, 0.75)',
            borderWidth: 0,
            maxBarThickness: 28,
          }],
        },
        options: {
          maintainAspectRatio: false,
          responsive: true,
          legend: { display: false },
          hover: {
            mode: 'nearest',
            intersect: true,
          },
          tooltips: {
            enabled: true,
            mode: 'nearest',
            intersect: true,
            position: 'nearest',
            caretPadding: 6,
            backgroundColor: 'rgba(38, 50, 56, 0.92)',
            callbacks: {
              title (tooltipItems) {
                if (!tooltipItems.length) return '';
                return tooltipItems[0].xLabel || '';
              },
              label (tooltipItem) {
                return `Открытия: ${tooltipItem.yLabel}`;
              },
            },
          },
          scales: {
            xAxes: [{
              gridLines: { display: false },
              ticks: {
                autoSkip: true,
                maxTicksLimit: 12,
              },
            }],
            yAxes: [{
              ticks: { beginAtZero: true, precision: 0 },
            }],
          },
        },
      });
    },
    destroyDailyChart () {
      if (!this.dailyChart) return;
      this.dailyChart.destroy();
      this.dailyChart = null;
    },
    destroyHourlyChart () {
      if (!this.hourlyChart) return;
      this.hourlyChart.destroy();
      this.hourlyChart = null;
    },
    destroyCharts () {
      this.destroyDailyChart();
      this.destroyHourlyChart();
    },
  },
};
</script>

<style lang="scss" scoped>
.ad-metrics-panel__header {
  margin-bottom: 8px;
  padding-bottom: 8px;
}

.ad-metrics-panel__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: 12px;
  min-width: 0;
}

.ad-metrics-panel__toolbar-main {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 0;
  min-width: 0;
  overflow: hidden;
}

.ad-metrics-panel__filters {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 10px;
  margin-left: 36px;
  min-width: 0;
}

.ad-metrics-panel__toolbar-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
}

.ad-metrics-panel__title {
  font-size: 1.25rem;
  font-weight: 500;
  color: #37474f;
  white-space: nowrap;
  flex-shrink: 0;
}

.ad-metrics-panel__hint {
  display: block;
  margin: 10px 0 16px;
  padding: 0;
  font-size: 0.8125rem;
  color: #78909c;
  line-height: 1.35;
}

.ad-metrics-panel__scope {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.ad-metrics-panel__scope-chip {
  display: inline-flex;
  align-items: center;
  max-width: 260px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(25, 118, 210, 0.08);
  color: #1565c0;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ad-metrics-panel__scope-chip .v-icon {
  color: #1976d2 !important;
}

.ad-metrics-panel__scope-city {
  color: #1565c0;
}

.ad-metrics-panel__scope-divider {
  margin: 0 4px;
  color: #90caf9;
  font-weight: 400;
}

.ad-metrics-panel__scope-region {
  color: #0d47a1;
}

.ad-metrics-panel__back-to-city {
  margin-left: 0;
}

.ad-metrics-panel__period-btn {
  min-width: 0 !important;
  height: 32px !important;
  padding: 0 8px !important;
  color: #546e7a !important;
  font-weight: 500;
  letter-spacing: 0;
  flex-shrink: 0;
}

.ad-metrics-panel__toggle {
  border: 1px solid rgba(25, 118, 210, 0.35);
  border-radius: 24px;
  overflow: hidden;
  background: #fff;
  flex-shrink: 0;
}

.ad-metrics-panel__toggle ::v-deep .v-btn {
  text-transform: none;
  font-weight: 500;
  min-width: 88px;
  border-radius: 0 !important;
}

.ad-metrics-panel__toggle ::v-deep .v-btn:not(.v-btn--active) {
  color: #1976d2;
  background-color: #fff !important;
}

.ad-metrics-panel__toggle ::v-deep .v-btn.v-btn--active {
  color: #fff !important;
  background-color: #1976d2 !important;
}

@media (max-width: 960px) {
  .ad-metrics-panel__toolbar {
    flex-wrap: wrap;
    align-items: stretch;
    gap: 10px;
  }

  .ad-metrics-panel__toolbar-main {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    overflow: visible;
    gap: 10px;
  }

  .ad-metrics-panel__title {
    white-space: normal;
    line-height: 1.3;
    font-size: 1.125rem;
  }

  .ad-metrics-panel__filters {
    margin-left: 0;
    width: 100%;
    flex-wrap: wrap;
    gap: 8px;
  }

  .ad-metrics-panel__toggle {
    width: 100%;
    display: flex;
  }

  .ad-metrics-panel__toggle ::v-deep {
    width: 100%;
  }

  .ad-metrics-panel__toggle ::v-deep .v-btn-toggle,
  .ad-metrics-panel__toggle.v-btn-toggle {
    width: 100%;
    display: flex;
  }

  .ad-metrics-panel__toggle ::v-deep .v-btn {
    flex: 1 1 0;
    min-width: 0 !important;
    padding: 0 6px !important;
    font-size: 0.8125rem;
  }

  .ad-metrics-panel__scope {
    order: 2;
  }

  .ad-metrics-panel__period-btn {
    order: 3;
  }

  .ad-metrics-panel__toolbar-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .ad-metrics-panel__hint {
    margin: 8px 0 14px;
  }
}

@media (max-width: 600px) {
  .ad-metrics-panel__title {
    font-size: 1.05rem;
  }

  .ad-metrics-panel__toggle ::v-deep .v-btn {
    font-size: 0.75rem;
    letter-spacing: 0;
  }

  .ad-metrics-panel__scope-chip {
    max-width: 100%;
  }

  .ad-metrics-panel__period-btn {
    padding-left: 4px !important;
    padding-right: 4px !important;
  }

  .ad-metrics-panel__toolbar-actions .v-btn {
    flex: 1 1 auto;
  }
}

.ad-metrics-panel__kpi-row {
  display: flex;
  flex-wrap: nowrap;
  width: 100%;
  margin-left: 0 !important;
  margin-right: 0 !important;
}

.ad-metrics-panel__kpi-col {
  flex: 1 1 0 !important;
  max-width: none !important;
  width: 0;
  padding: 4px !important;
}

.ad-metrics-panel__kpi {
  border-radius: 12px;
  height: 100%;
}

.ad-metrics-panel__kpi-body {
  padding: 12px 10px !important;
}

.ad-metrics-panel__kpi-title {
  font-size: 0.75rem;
  color: #78909c;
  margin-bottom: 6px;
  line-height: 1.25;
  min-height: 2.4em;
}

.ad-metrics-panel__kpi-value {
  font-size: 1.35rem;
  font-weight: 600;
  color: #0d4092;
  line-height: 1.2;
}

.ad-metrics-panel__kpi-hint {
  margin-top: 4px;
  font-size: 0.7rem;
  color: #90a4ae;
}

@media (max-width: 1264px) {
  .ad-metrics-panel__kpi-row {
    flex-wrap: wrap;
  }

  .ad-metrics-panel__kpi-col {
    flex: 1 1 25% !important;
    max-width: 25% !important;
    width: auto;
  }
}

@media (max-width: 960px) {
  .ad-metrics-panel__kpi-col {
    flex: 1 1 50% !important;
    max-width: 50% !important;
  }
}

@media (max-width: 600px) {
  .ad-metrics-panel__kpi-col {
    flex: 1 1 100% !important;
    max-width: 100% !important;
  }
}

.ad-metrics-panel__kpi-note {
  margin: 0 0 16px;
  font-size: 0.8125rem;
  color: #90a4ae;
}

.ad-metrics-panel__chart-card {
  border-radius: 14px;
}

.ad-metrics-panel__regions {
  margin-top: 4px;
}

.ad-metrics-panel__regions-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 16px;
}

.ad-metrics-panel__regions-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 0;
  font-size: 0.8125rem;
  color: #78909c;
  text-align: right;
}

.ad-metrics-panel__regions-meta-date {
  color: #1976d2;
  font-weight: 500;
}

.ad-metrics-panel__regions-meta-sep {
  margin: 0 6px;
  color: #b0bec5;
}

.ad-metrics-panel__regions-header-right {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  justify-content: flex-end;
}

.ad-metrics-panel__region-card {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  height: 100%;
  cursor: pointer;
  transition: box-shadow 0.15s ease, transform 0.15s ease, border-color 0.15s ease;
}

.ad-metrics-panel__region-card--active {
  border-color: #1976d2 !important;
  box-shadow: 0 0 0 1px rgba(25, 118, 210, 0.35), 0 4px 12px rgba(25, 118, 210, 0.12);
}

.ad-metrics-panel__region-card:hover {
  box-shadow: 0 4px 12px rgba(38, 50, 56, 0.08);
  transform: translateY(-1px);
}

.ad-metrics-panel__region-accent {
  height: 3px;
  width: 100%;
}

.ad-metrics-panel__region-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}

.ad-metrics-panel__region-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #37474f;
  line-height: 1.2;
}

.ad-metrics-panel__region-share-chip {
  flex-shrink: 0;
  height: 20px !important;
  font-size: 0.65rem !important;
}

.ad-metrics-panel__region-metrics {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 12px;
  padding: 10px 6px;
  border-bottom: 1px solid #eef2f6;
  background: #f8fafc;
  border-radius: 8px;
}

@media (max-width: 600px) {
  .ad-metrics-panel__region-metrics {
    grid-template-columns: repeat(2, 1fr);
  }
}

.ad-metrics-panel__region-metric {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 4px;
  width: 100%;
  border-radius: 8px;
  padding: 8px 4px;
  transition: background-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.ad-metrics-panel__region-metric--interactive {
  cursor: default;
}

.ad-metrics-panel__region-metric--interactive:hover {
  background: #fff;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.12);
  transform: translateY(-1px);
}

.ad-metrics-panel__region-metric-value {
  font-size: 1.375rem;
  font-weight: 700;
  color: #0d4092;
  line-height: 1.1;
}

.ad-metrics-panel__region-metric-label {
  font-size: 0.8125rem;
  color: #546e7a;
  line-height: 1.2;
  font-weight: 500;
}

.ad-metrics-panel__region-metric-share {
  font-size: 0.75rem;
  font-weight: 600;
  color: #1976d2;
  line-height: 1;
  margin-top: 2px;
}

.ad-metrics-panel__region-activity {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
  margin-bottom: 12px;
}

@media (max-width: 600px) {
  .ad-metrics-panel__region-activity {
    grid-template-columns: repeat(2, 1fr);
  }
}

.ad-metrics-panel__region-activity-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 8px;
  border-radius: 8px;
  background: #f5f7fa;
}

.ad-metrics-panel__region-activity-label {
  font-size: 0.6875rem;
  color: #78909c;
  line-height: 1.2;
}

.ad-metrics-panel__region-activity-value {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #37474f;
  line-height: 1.2;
}

.ad-metrics-panel__region-bars {
  margin-top: 0;
}

.ad-metrics-panel__region-bar-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.6875rem;
  color: #78909c;
  margin-bottom: 2px;
  line-height: 1.2;
}

.ad-metrics-panel__empty--regions {
  height: 100px;
}

.ad-metrics-panel__chart-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
}

.ad-metrics-panel__chart-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 500;
  color: #455a64;
}

.ad-metrics-panel__day-select {
  max-width: 200px;
}

.ad-metrics-panel__series-chip {
  cursor: pointer;
  user-select: none;
}

.ad-metrics-panel__series-chip--off {
  opacity: 0.7;
  text-decoration: line-through;
}

.ad-metrics-panel__canvas-wrap {
  position: relative;
  height: 320px;
}

.ad-metrics-panel__canvas-wrap--short {
  height: 280px;
}

.ad-metrics-panel__empty {
  height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  color: #90a4ae;
  text-align: center;
  padding: 0 16px;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 10px;
}

.ad-metrics-panel__empty--short {
  height: 240px;
}
</style>

<style lang="scss">
.ad-metrics-panel__period-menu {
  background: #fff !important;
  z-index: 220 !important;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.18) !important;
  border-radius: 8px;
  overflow: hidden;
}

.ad-metrics-panel__period-menu-body {
  background: #fff;
}
</style>
